'use client'

import { BUTTON_COLOR_PRIMARY, DOPPEL_GREY_300 } from '@/utils/style'
import { Box } from '@chakra-ui/react'

import {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
  SetStateAction,
  Dispatch,
} from 'react'
import {
  getNodeStyle,
  LINK_TYPE_TO_ARROW_CONFIG,
  THREAT_NODE_STATUS_MAP,
  ThreatGraphLink,
  ThreatGraphLinkExtended,
  ThreatGraphNodeExtended,
  useNodeIcons,
  ThreatGraphFilters,
} from '@/components/threat_graph/threat_graph_utils'
import { PROGRESSIVE_WARP_PATTERN } from '@/utils/image'
import { Archetype, Platform, ThreatLinkCategory } from '@/generated/enums'
import CenteredSpinner from '@/components/shared/centered_spinner'
import { useIsEmployeeView } from '@/hooks/id_token_claims'

// same module imports
import {
  LinkDetailPopover,
  LinkHoverTooltip,
  NodeDetailPopover,
  NodeHoverPopover,
} from './threat_graph_popovers'

import loadable from '@loadable/component'
import { useSize } from '@/hooks/use_size'
import { ApolloError } from '@apollo/client'
import ErrorComponent from '@/components/error_component'
const ForceGraph2D = loadable(() => import('react-force-graph-2d'), { ssr: false })

type ThreatGraphProps = {
  nodes: ThreatGraphNodeExtended[]
  links: ThreatGraphLinkExtended[]
  loading: boolean
  error?: ApolloError
  height?: string
  width?: string
  setHoveredNodeRef?: React.MutableRefObject<
    Dispatch<SetStateAction<ThreatGraphNodeExtended | null>>
  >
  setClickedNodeRef?: React.MutableRefObject<
    Dispatch<SetStateAction<ThreatGraphNodeExtended | null>>
  >
  openAddModal?: (node: ThreatGraphNodeExtended) => void // if not provided, will not show add button(s)
  openDeleteModal?: (link: ThreatGraphLink) => void
  allowZoom?: boolean
  threatGraphFilters?: ThreatGraphFilters
}

const NODE_SIZE = 6

const drawNode = (
  node: ThreatGraphNodeExtended,
  getNodeIcon: (archetype: Archetype, platform: Platform) => HTMLImageElement | null,
  ctx: CanvasRenderingContext2D,
  globalScale: number,
): void => {
  const nodeRadius = NODE_SIZE / globalScale

  // Add glow effect for currentNode
  const isCurrent = node.currentNode

  if (isCurrent) {
    ctx.shadowColor = BUTTON_COLOR_PRIMARY
    ctx.shadowBlur = 20
  }

  const nodeStyle = getNodeStyle(node)
  const { fillColor, borderColor } = THREAT_NODE_STATUS_MAP[nodeStyle]

  const img = getNodeIcon(
    node.entity?.archetype as Archetype,
    node.entity?.subtypeMetadata?.platform,
  )

  // Draw a circle background
  ctx.beginPath()
  ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false)
  ctx.fillStyle = fillColor
  ctx.fill()
  if (img) {
    // If the image was successfully pre-loaded
    // Draw the pre-loaded image
    ctx.drawImage(
      img,
      node.x - nodeRadius,
      node.y - nodeRadius,
      nodeRadius * 2,
      nodeRadius * 2,
    )
  }

  // Draw the border if specified for the style, around the icon/fallback circle
  if (borderColor) {
    ctx.beginPath() // Start a new path for the border
    ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI, false)
    ctx.strokeStyle = borderColor
    ctx.lineWidth = 1 / globalScale // 2px border in screen space
    ctx.stroke()
  }

  // Reset shadow *after* drawing to not affect other elements
  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0
}

const drawLink = (
  link: ThreatGraphLinkExtended,
  hoveredLink: ThreatGraphLinkExtended | null,
  ctx: CanvasRenderingContext2D,
  globalScale: number,
): void => {
  const { source, target } = link
  const linkData = link

  // --- Configuration ---
  const config =
    LINK_TYPE_TO_ARROW_CONFIG[linkData.linkCategory || ThreatLinkCategory.REFERENCE]
  const nodeRadius = NODE_SIZE / globalScale // Radius of the node representation (adjust if needed)
  const lineWidth = 1 / globalScale
  const dashedInterval = 2 / globalScale // Length of dashes
  const arrowLength = 5 / globalScale // Tip to base length of the arrowhead
  const arrowOffset = config.hasArrow ? arrowLength : 0
  const arrowWidth = 4 / globalScale // Arrowhead base width

  // --- Determine State ---
  const isHovered =
    hoveredLink &&
    source.id === hoveredLink.source?.id &&
    target.id === hoveredLink.target?.id
  const color = isHovered ? BUTTON_COLOR_PRIMARY : DOPPEL_GREY_300

  // --- Calculations ---
  const diffX = target.x - source.x
  const diffY = target.y - source.y
  const dist = Math.sqrt(diffX * diffX + diffY * diffY)

  if (dist <= nodeRadius * 2 + arrowOffset) return // Avoid drawing if too short or overlapping

  const normX = diffX / dist
  const normY = diffY / dist

  // Adjust start/end points to node edges
  const startX = source.x + normX * nodeRadius
  const startY = source.y + normY * nodeRadius
  // End point adjusted for arrowhead offset
  const endX = target.x - normX * (nodeRadius + arrowOffset)
  const endY = target.y - normY * (nodeRadius + arrowOffset)

  // --- Draw Line ---
  ctx.save() // Save context state
  ctx.beginPath()
  ctx.moveTo(startX, startY)
  ctx.lineTo(endX, endY)
  ctx.strokeStyle = color
  ctx.lineWidth = lineWidth
  ctx.setLineDash(config.isDashed ? [dashedInterval, dashedInterval] : [])
  ctx.stroke()
  ctx.setLineDash([]) // Reset dashes
  ctx.restore() // Restore context state

  // --- Draw Arrowhead ---
  if (config.hasArrow) {
    // tip of the arrow
    const arrowBaseX = target.x - normX * nodeRadius
    const arrowBaseY = target.y - normY * nodeRadius

    ctx.save()
    ctx.beginPath()
    ctx.moveTo(arrowBaseX, arrowBaseY)
    ctx.lineTo(
      arrowBaseX - normX * arrowLength + (normY * arrowWidth) / 2,
      arrowBaseY - normY * arrowLength - (normX * arrowWidth) / 2,
    )
    ctx.lineTo(
      arrowBaseX - normX * arrowLength - (normY * arrowWidth) / 2,
      arrowBaseY - normY * arrowLength + (normX * arrowWidth) / 2,
    )
    ctx.closePath()
    ctx.fillStyle = color
    ctx.fill()
    ctx.restore()
  }
}

/**
 * Visual component to render a threat graph.
 *
 * Node/link objects will be mutated-- to make use of the x/y positions computed
 * by react-force-graph-2d, ensure the parent keeps the same references to the
 * nodes/links arrays and their objects.
 */
const ThreatGraph: React.FC<ThreatGraphProps> = ({
  nodes,
  links,
  loading,
  error,
  height = '100%',
  width = '100%',
  setHoveredNodeRef,
  setClickedNodeRef,
  openAddModal,
  openDeleteModal,
  allowZoom = false,
  threatGraphFilters = null,
}) => {
  const isLoading = loading || !ForceGraph2D
  const graphRef = useRef<any>()
  const graphInitialized = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { width: containerWidth, height: containerHeight } = useSize(containerRef)

  const getNodeIcon = useNodeIcons()

  const [isEmployeeView] = useIsEmployeeView()

  // memoize, as react-force-graph-2d mutates the result
  const graphData = useMemo(() => {
    // these are temporary, as react-force-graph-2d will replace them with full objects
    links.forEach((link) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      link.source = link.parent
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      link.target = link.child
    })
    return { nodes, links }
  }, [nodes, links])

  const [hoveredLink, setHoveredLink] = useState<ThreatGraphLinkExtended | null>(null)
  const [hoveredNode, setHoveredNode] = useState<ThreatGraphNodeExtended | null>(null)
  const [clickedNode, setClickedNode] = useState<ThreatGraphNodeExtended | null>(null)
  const [clickedLink, setClickedLink] = useState<ThreatGraphLinkExtended | null>(null)

  useEffect(() => {
    if (setHoveredNodeRef) {
      setHoveredNodeRef.current = setHoveredNode
    }
    if (setClickedNodeRef) {
      setClickedNodeRef.current = setClickedNode
    }
  }, [setHoveredNodeRef, setClickedNodeRef])

  const handleNodeClick = useCallback((node) => {
    setClickedNode((prevNode) => (node === prevNode ? null : node))
  }, [])

  const handleLinkClick = useCallback((link) => {
    setClickedLink((prevLink) => (link === prevLink ? null : link))
  }, [])

  const handleNodeHover = useCallback((node, prevNode) => {
    if (node?.id === prevNode?.id) return
    setHoveredNode(node)
  }, [])

  const handleLinkHover = useCallback((link, prevLink) => {
    if (
      link?.source?.id === prevLink?.source?.id &&
      link?.target?.id === prevLink?.target?.id
    ) {
      return
    }
    setHoveredLink(link)
  }, [])

  const initializeGraph = (graphObj: any) => {
    // handle multiple subgraphs without making too far for global view
    graphObj.d3Force('charge').distanceMax(100)
    // pan graph view so its content appears shifted to the left
    graphObj.centerAt(50, 0, 0) // in graph units
  }

  const innerContent = (() => {
    if (error) {
      return (
        <ErrorComponent
          errorText="Error loading threat graph"
          height="100%"
          iconSize={8}
          stackDirection="column"
        />
      )
    }
    if (isLoading) {
      return <CenteredSpinner />
    }
    return (
      <>
        <ForceGraph2D
          cooldownTicks={100}
          graphData={graphData}
          height={containerHeight}
          linkCanvasObject={(link, ctx) => {
            // use (link, ctx, globalScale) if sizes need to stay constant with zoom
            drawLink(link, hoveredLink, ctx, 1)
          }}
          linkDirectionalArrowLength={0}
          linkDirectionalArrowRelPos={1}
          linkDirectionalParticles={0}
          maxZoom={allowZoom ? undefined : 2} // zoom = 2 for detail view
          minZoom={allowZoom ? undefined : 2}
          nodeCanvasObject={(node, ctx) => {
            // use (node, ctx, globalScale) if sizes need to stay constant with zoom
            drawNode(node, getNodeIcon, ctx, 1)
          }}
          nodeRelSize={NODE_SIZE}
          onLinkClick={handleLinkClick}
          onLinkHover={handleLinkHover}
          onNodeClick={handleNodeClick}
          onNodeDragEnd={(node) => {
            // fix nodes after dragging
            node.fx = node.x
            node.fy = node.y
          }}
          onNodeHover={handleNodeHover}
          onRenderFramePre={() => {
            if (graphRef.current && !graphInitialized.current) {
              initializeGraph(graphRef.current)
              graphInitialized.current = true
            }
          }}
          ref={graphRef}
          width={containerWidth}
        />

        {hoveredNode && hoveredNode.id !== clickedNode?.id && (
          <NodeHoverPopover graphRef={graphRef} node={hoveredNode} />
        )}

        {clickedNode && (
          <NodeDetailPopover
            addModalAction={
              isEmployeeView && openAddModal
                ? () => openAddModal(nodes.find((n) => n.id === clickedNode?.id))
                : undefined
            }
            graphRef={graphRef}
            node={clickedNode}
            onClose={() => setClickedNode(null)}
          />
        )}

        {clickedLink && (
          <LinkDetailPopover
            graphRef={graphRef}
            link={clickedLink}
            onClose={() => setClickedLink(null)}
            openDeleteModal={(link) => openDeleteModal(link)}
          />
        )}

        {hoveredLink && hoveredLink.id !== clickedLink?.id && (
          <LinkHoverTooltip graphRef={graphRef} link={hoveredLink} />
        )}
      </>
    )
  })()

  return (
    <Box
      bgImage={PROGRESSIVE_WARP_PATTERN}
      bgPosition="center"
      bgRepeat="no-repeat"
      bgSize="cover"
      cursor={isLoading || hoveredLink ? 'default' : 'grab'}
      display="flex"
      flexDirection="column"
      height={height}
      padding={0}
      position="relative"
      ref={containerRef}
      width={width}
    >
      {innerContent}
    </Box>
  )
}

export default ThreatGraph
